/**
 * Detect a MapLibre `error` event that means the MapTiler API key was REJECTED
 * (present but 401/403) — as opposed to a missing key (handled at startup) or a
 * transient network blip. MapLibre does NOT reliably populate `e.error.status`
 * for raster-tile load failures, which is why CL-243's status-only check never
 * fired (R-3 / BUG-071). We also inspect the error message and URL.
 *
 * Kept in its own module (no maplibre-gl import) so it is unit-testable in node.
 */
export function isMapTilerKeyRejection(e) {
  const err = e && e.error
  const src = (e && e.sourceId) || ''
  const msg = err ? String(err.message || err) : ''
  const url = (err && (err.url || (err.request && err.request.url))) || ''
  const status = err && (err.status != null ? err.status : err.statusCode)
  const isMapTiler = src.startsWith('maptiler-') || /maptiler/i.test(url) || /maptiler/i.test(msg)
  const isAuthReject =
    status === 401 ||
    status === 403 ||
    /(^|[^0-9])(401|403)([^0-9]|$)/.test(msg) ||
    /forbidden|unauthorized|invalid key/i.test(msg)
  return Boolean(isMapTiler && isAuthReject)
}

/**
 * Keyless fallback basemap (CL-261). When the MapTiler key is missing or
 * rejected the globe used to render black; these public-domain / open sources
 * need no key and send CORS headers, so the globe always has imagery + relief.
 *   - Imagery: NASA GIBS Blue Marble shaded relief + bathymetry (public domain,
 *     native max z8 — MapLibre overzooms beyond that).
 *   - Terrain: AWS Terrain Tiles (Mapzen terrarium encoding, open data).
 * Source ids stay `maptiler-*` so every setTerrain({ source: 'maptiler-terrain' })
 * call site keeps working after the swap.
 */
export const FALLBACK_SATELLITE_SOURCE = {
  type: 'raster',
  tiles: [
    'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/BlueMarble_ShadedRelief_Bathymetry/default/GoogleMapsCompatible_Level8/{z}/{y}/{x}.jpeg',
  ],
  tileSize: 256,
  maxzoom: 8,
  attribution: 'Imagery: NASA Blue Marble (GIBS)',
}

export const FALLBACK_TERRAIN_SOURCE = {
  type: 'raster-dem',
  tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
  encoding: 'terrarium',
  tileSize: 256,
  maxzoom: 14,
  attribution: 'Terrain: Mapzen / AWS Terrain Tiles',
}
